import "../alert/alert";
import "../checkbox/checkbox";
import "../textfield/phone-field";
import "../textfield/textfield";
import "../field-container/field-container";
import "../button/button";
import "../progress-circle/progress-circle";

import { type CSSResultArray, html, LitElement, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { msg } from "@lit/localize";

import styles from "./contact-form.scss";
import grid from "../../styles/layout/_grid.scss";
import { getConfiguration } from "../../utils/configuration";
import { FormControl } from "../../forms/form-control";
import { email, required } from "../../forms/validators";
import { FormController } from "../../forms/form-controller";
import { sendContactFormMessage } from "../../api/contact-form-message/send-contact-form-message";

declare interface ContactFormValue {
	name: string;
	email: string;
	phone: string;
	message: string;
	agree: boolean;
}

const actionLegalPersonalData = getConfiguration().actions["legal/personalData"];

@customElement("oz-contact-form")
export class ContactForm extends LitElement {
	public static override styles: CSSResultArray = [grid, styles];

	private readonly _form = this.createForm();

	@state()
	private _sending: boolean = false;

	@state()
	private _sendState: boolean | null = null;

	@state()
	private _invalid: boolean = false;

	protected override render(): TemplateResult {
		return html`
			<oz-field-container>
				<oz-textfield label="${msg("Jméno a příjmení")}" required ${this._form.attach("name")}></oz-textfield>
			</oz-field-container>

			<div class="row">
				<div class="col-md-6">
					<oz-field-container>
						<oz-textfield label="${msg("E-mail")}" required type="email" ${this._form.attach("email")}></oz-textfield>
					</oz-field-container>
				</div>
				<div class="col-md-6">
					<oz-field-container>
						<oz-phone-field label="${msg("Telefon")}" required ${this._form.attach("phone")}></oz-phone-field>
					</oz-field-container>
				</div>
			</div>

			<oz-field-container>
				<oz-textfield label="${msg("Váš dotaz")}" multiline required ${this._form.attach("message")}></oz-textfield>
			</oz-field-container>

			<oz-field-container>
				<oz-checkbox required ${this._form.attach("agree")}>
					${msg(html`Souhlasím se zpracováním <a href="${actionLegalPersonalData}" target="_blank">osobních údajů</a>`)}
				</oz-checkbox>
			</oz-field-container>

			${
				this._sendState !== null
					? html`
				${
					this._sendState
						? html`
					<oz-alert variant="success">
						${msg("Vaše zpráva byla úspěšně odeslána!")}
					</oz-alert>
				`
						: html`
					<oz-alert variant="negative">
						${msg(html`Zprávu se nepodařilo odeslat. V případě přetrvávajících problémů, prosím kontaktujte naši podporu na <a href="mailto:info@odhad-zdarma.cz">info@odhad-zdarma.cz</a>. Děkujeme.`)}
					</oz-alert>
				`
				}
			`
					: ""
			}

			<div>
				${
					this._sending
						? html`
					<oz-progress-circle size="l"></oz-progress-circle>
				`
						: html`
					<oz-button variant="${this._invalid ? "negative" : "success"}" @click="${this.handleSubmit}">${msg("ODESLAT DOTAZ")}</oz-button>
				`
				}
			</div>
		`;
	}

	private createForm(): FormController<ContactFormValue> {
		const form = new FormController<ContactFormValue>(this, {
			name: new FormControl("", [required]),
			email: new FormControl("", [required, email]),
			phone: new FormControl("", [required]),
			message: new FormControl("", {
				keepNewLines: true,
				validators: [required],
			}),
			agree: new FormControl<boolean>(false, [required]),
		});

		form.valueChanged.subscribe(() => (this._invalid = false));

		return form;
	}

	private async handleSubmit(): Promise<void> {
		this._invalid = false;

		const value = this._form.submit();
		if (value === null) {
			this._invalid = true;
			return;
		}

		this._sending = true;
		this._sendState = null;

		dataLayer.push({
			event: "contact_form",
			language_code: getConfiguration().languageCode,
			user_name: value.name,
			user_email: value.email,
			user_phone: value.phone,
			message: value.message,
		});

		try {
			await sendContactFormMessage({
				name: value.name,
				email: value.email,
				phone: value.phone,
				message: value.message,
			});
		} catch (e) {
			this._sendState = false;
			throw e;
		} finally {
			this._sending = false;
		}

		this._sendState = true;
		setTimeout(() => {
			this._sendState = null;
		}, 150000000);

		this._form.reset();
	}
}
